import { EyeIcon, EyeOffIcon } from 'lucide-react'
import React, { useState } from 'react'

import { formatMaskedSSNForDisplay } from 'components/member_onboarding_v2/utils'
import { renderFormattedSSN } from 'components/members/utils'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { IconButton } from 'slab'
import { capitalize, cn, getFormattedDate, snakeToSentenceCase } from 'utils'
import {
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdOnboardingRevealMemberSsn,
} from 'utils/web_api'
import { type MemberOnboardingMember } from 'utils/web_api.schemas'

import { useOnboarding } from '../../OnboardingContext'
import { NewYorkOrganDonorQuestionField } from '../NewYorkOrganDonorQuestionField'
import RenewalsMemberInfoEditForm, {
  LABEL_CONTAINER_CLASSES,
} from '../RenewalsMemberInfoEditForm/RenewalsMemberInfoEditForm'

/** Response shape from reveal_member_ssn endpoint */
interface RevealMemberSsnResponse {
  ssn: string;
}

interface RenewalsMemberInfoProps {
  member: MemberOnboardingMember;
  missingFields: Array<string>;
  handleEditOpen: () => void;
  handleEditSuccessful: () => void;
  handleEditCancel: () => void;
  isMissingHouseholdIncome: boolean;
}

const RenewalsMemberInfo: React.FC<RenewalsMemberInfoProps> = ({
  member,
  missingFields,
  isMissingHouseholdIncome,
  handleEditOpen,
  handleEditSuccessful,
  handleEditCancel,
}) => {
  const { enrollmentEventId } = useOnboarding()
  const [showFullSSN, setShowFullSSN] = useState(false)

  const {
    data: revealedSsnData,
    isLoading: isRevealSsnLoading,
  } = useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdOnboardingRevealMemberSsn<RevealMemberSsnResponse>(
    enrollmentEventId,
    {
      query: {
        enabled: showFullSSN && !!enrollmentEventId,
      },
    }
  )

  const toggleSSNVisibility = () => {
    setShowFullSSN(!showFullSSN)
  }

  const occupation = snakeToSentenceCase(member.occupation ?? '')

  return (
    <div className='border border-gray-200 shadow-sm rounded-lg'>
      <div className='p-4 rounded-t-lg border-b border-gray-200 bg-thatch-50'>
        <div className='flex justify-between items-center gap-2 mb-4'>
          <div className='flex items-center gap-2'>
            <h3 className='body-bold sensitive'>
              {member.given_name} {member.family_name}
            </h3>
            <span className='body text-gray-500'>Self</span>
          </div>
        </div>
        <div className='flex flex-col gap-y-4'>
          <div className='grid grid-cols-2 xs:grid-cols-3 gap-4'>
            <div>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>Date of birth</span>
              </div>
              <div className='body sensitive'>
                {getFormattedDate(member.date_of_birth + 'T00:00:00')}
              </div>
            </div>

            <div>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>SSN</span>
                {member.masked_ssn && (
                  <IconButton
                    icon={showFullSSN ? EyeOffIcon : EyeIcon}
                    label={showFullSSN ? 'Hide SSN' : 'Show SSN'}
                    onClick={toggleSSNVisibility}
                    disabled={isRevealSsnLoading}
                  />
                )}
              </div>
              {member.masked_ssn
                ? (
                  <div className='body font-mono sensitive'>
                    {showFullSSN && isRevealSsnLoading
                      ? (
                        <SkeletonItem heightClass='h-3' widthClass='w-24' />
                        )
                      : showFullSSN && revealedSsnData?.ssn
                        ? (
                            renderFormattedSSN(revealedSsnData.ssn, true)
                          )
                        : (
                            formatMaskedSSNForDisplay(member.masked_ssn)
                          )}
                  </div>
                  )
                : (
                  <div className='body'>—</div>
                  )}
            </div>

            <div>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>Gender</span>
              </div>
              <div className='body sensitive'>
                {capitalize(member.gender ?? '').replace('_', '-')}
              </div>
            </div>
            <div className={cn(occupation.length > 25 && 'col-span-2')}>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>Occupation</span>
              </div>
              <div className='body'>{occupation}</div>
            </div>

            <div>
              <div className={LABEL_CONTAINER_CLASSES}>
                <span>Citizenship status</span>
              </div>
              <div className='body sensitive'>
                {member.is_us_citizen
                  ? 'American citizen'
                  : 'Not an American citizen'}
              </div>
            </div>

            <NewYorkOrganDonorQuestionField memberInfo={member} missingFields={missingFields} />
          </div>
        </div>
      </div>
      <RenewalsMemberInfoEditForm
        member={member}
        onEditOpen={handleEditOpen}
        onSave={handleEditSuccessful}
        onCancel={handleEditCancel}
        isMissingHouseholdIncome={isMissingHouseholdIncome}
      />
    </div>
  )
}

export default RenewalsMemberInfo
